import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Navigation, ShieldCheck, Truck, Clock } from "lucide-react";
import SEO from "../components/SEO";
import { NEIGHBORHOODS, NEIGHBORHOOD_LIST } from "../lib/neighborhoods";
import { BRAND, ACTIVE_LOCATIONS } from "../lib/constants";
import NotFound from "./NotFound";

export default function NeighborhoodPage() {
  const { slug } = useParams();
  // The router passes `slug` which is something like "dispensary-petworth-dc".
  // Our data keys are short ids like "petworth"; look up by `slug` field on entries.
  const data = NEIGHBORHOOD_LIST.find((n) => n.slug === slug);

  if (!data) return <NotFound />;

  const flagship = ACTIVE_LOCATIONS[0];

  // Canonical strategy: Petworth is the primary/canonical neighborhood page.
  // Georgia Ave NW is a near-duplicate covering the same store — canonicalize
  // it to Petworth to avoid Google flagging duplicate content.
  const CANONICAL_ALIASES = {
    "dispensary-georgia-ave-nw": "/dispensary-petworth-dc",
  };
  const canonicalOverride = CANONICAL_ALIASES[data.slug];

  return (
    <>
      <SEO
        title={`${data.name} Dispensary | District Wellness Center · ${flagship.street}`}
        description={data.metaDescription}
        path={`/${data.slug}`}
        canonical={canonicalOverride}
      />

      {/* HERO */}
      <section className="bg-white relative" data-testid={`hood-hero-${data.slug}`}>
        <div className="absolute inset-0 opacity-[0.035] pointer-events-none"
             style={{backgroundImage: "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)", backgroundSize: "60px 60px"}} />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 sm:pt-28 sm:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">{data.fullName}</div>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              {data.h1Lead} <em className="text-brand">{data.h1Highlight}</em>.
            </h1>
            <p className="mt-8 text-lg text-ink-muted max-w-2xl leading-relaxed">
              {data.intro}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/menu" className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors" data-testid={`hood-${data.slug}-menu`}>
                Order Online <ArrowUpRight size={14} />
              </Link>
              <Link to="/delivery" className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-black text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors" data-testid={`hood-${data.slug}-delivery`}>
                <Truck size={14} /> {data.name} Delivery
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-[11px] uppercase tracking-[0.2em] text-ink-muted">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-brand" /> DC ABCA License {BRAND.licenseNumber}</span>
              <span className="inline-flex items-center gap-2"><Clock size={12} className="text-brand" /> Sun–Mon &amp; Wed–Sat 12–8pm</span>
              <span className="inline-flex items-center gap-2"><MapPin size={12} className="text-brand" /> {flagship.street}, {flagship.city}</span>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="border border-black/10 overflow-hidden h-72 lg:h-96 relative">
              <iframe
                title={`Map to DWC from ${data.name}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(flagship.mapQuery)}&output=embed`}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                data-testid={`hood-map-${data.slug}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* BODY SECTIONS */}
      <article className="bg-[#FAFAFA] py-20">
        <div className="max-w-4xl mx-auto px-6">
          {data.bodySections.map((s, i) => (
            <section key={i} className="mb-14 last:mb-0" data-testid={`hood-${data.slug}-section-${i}`}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">0{i + 1}</div>
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-6 text-ink">{s.h2}</h2>
              <div className="space-y-5 text-base sm:text-lg leading-relaxed text-ink-muted">
                {s.copy.map((p, j) => <p key={j}>{p}</p>)}
              </div>
            </section>
          ))}
        </div>
      </article>

      {/* COMPLIANCE BAND */}
      <section className="bg-white py-16 border-y border-black/10">
        <div className="max-w-5xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="border border-black/10 p-6">
            <ShieldCheck size={18} className="text-brand mb-3" />
            <div className="font-serif text-xl leading-tight">DC ABCA Licensed</div>
            <div className="text-sm text-ink-muted mt-2">License {BRAND.licenseNumber}</div>
          </div>
          <div className="border border-black/10 p-6">
            <MapPin size={18} className="text-brand mb-3" />
            <div className="font-serif text-xl leading-tight">{flagship.street}</div>
            <div className="text-sm text-ink-muted mt-2">{flagship.city}</div>
          </div>
          <div className="border border-black/10 p-6">
            <Truck size={18} className="text-brand mb-3" />
            <div className="font-serif text-xl leading-tight">Same-Day Delivery</div>
            <div className="text-sm text-ink-muted mt-2">to all of {data.name}</div>
          </div>
          <div className="border border-black/10 p-6">
            <Clock size={18} className="text-brand mb-3" />
            <div className="font-serif text-xl leading-tight">Open 7 days</div>
            <div className="text-sm text-ink-muted mt-2">8am – late</div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-ink text-white py-20 relative">
        <div className="grain absolute inset-0 opacity-50 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">{data.name} · {flagship.label}</div>
          <h2 className="font-serif text-4xl sm:text-6xl leading-[0.95]">{data.ctaTitle}</h2>
          <p className="mt-6 text-white/70 text-lg">{data.ctaCopy}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(flagship.mapQuery)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]"
              data-testid={`hood-${data.slug}-directions`}
            >
              <Navigation size={14} /> Directions from {data.name}
            </a>
            <a href={BRAND.phoneHref} className="inline-flex items-center justify-center gap-2 bg-white text-ink hover:bg-brand hover:text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid={`hood-${data.slug}-call`}>
              Call {BRAND.phone}
            </a>
          </div>

          {/* Internal links to sister neighborhoods */}
          <div className="mt-16 pt-10 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-5">Other DC neighborhoods we serve</div>
            <div className="flex flex-wrap gap-2 justify-center">
              {NEIGHBORHOOD_LIST.filter((n) => n.slug !== data.slug).map((n) => (
                <Link
                  key={n.slug}
                  to={`/${n.slug}`}
                  className="px-4 py-2 border border-white/20 hover:border-brand hover:text-brand text-sm"
                  data-testid={`hood-${data.slug}-related-${n.slug}`}
                >
                  {n.name}
                </Link>
              ))}
              <Link to="/areas-served" className="px-4 py-2 border border-white/20 hover:border-brand hover:text-brand text-sm">
                See all areas →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// Export for use elsewhere (e.g., to know if a URL is a neighborhood page)
export { NEIGHBORHOODS };
